/**
 * Cross-feature context, so it lives outside `app/features/`: the record page
 * (`views`) and the composed-page tab path (`navigation`) both read it.
 */
import { createContext, type ReactNode, use, useCallback, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

import { liveSearchParams } from "~/lib/liveSearchParams";

type SetRecordSearchParams = (next: URLSearchParams, options?: { replace?: boolean }) => void;

export type RecordSearchParams = readonly [URLSearchParams, SetRecordSearchParams];

const RecordLocationContext = createContext<RecordSearchParams | null>(null);

/**
 * Holds a record surface's `?tab=` / `?view=` / `?detailPage=` state in memory.
 *
 * The record peek renders the record page inside a list page, where those
 * params already belong to the collection (`?view=` is the table view) — so a
 * tab click in the peek must not rewrite the list page's URL.
 */
export const InMemoryRecordLocation = ({ children }: { children: ReactNode }) => {
  const [params, setParams] = useState(() => new URLSearchParams());
  const setRecordParams = useCallback<SetRecordSearchParams>((next) => {
    setParams(new URLSearchParams(next));
  }, []);
  const value = useMemo(() => [params, setRecordParams] as const, [params, setRecordParams]);

  return <RecordLocationContext value={value}>{children}</RecordLocationContext>;
};

/** `useSearchParams` for record surfaces — the router's, unless an in-memory location wraps it. */
export const useRecordSearchParams = (): RecordSearchParams => {
  const routed = useSearchParams();

  return use(RecordLocationContext) ?? routed;
};

/**
 * Copy of the params the next write builds on. An in-memory location keeps its own; under the
 * router this is the address bar (see `liveSearchParams`), not the list page behind a peek.
 */
export const useRecordWriteParams = (): (() => URLSearchParams) => {
  const inMemory = use(RecordLocationContext);
  const [routed] = useSearchParams();
  const { pathname } = useLocation();
  const ownParams = inMemory?.[0];

  return useCallback(
    () => (ownParams ? new URLSearchParams(ownParams) : liveSearchParams(routed, pathname)),
    [ownParams, routed, pathname],
  );
};
